import { useState, useRef, useCallback } from 'react'

const THRESHOLD = 80
const MAX_PULL = 120

function PullToRefresh({ onRefresh, children }) {
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const startY = useRef(0)
  const pulling = useRef(false)
  const containerRef = useRef(null)

  const onTouchStart = useCallback((e) => {
    if (refreshing) return
    // Only activate when scrolled to top
    const scrollTop = containerRef.current?.scrollTop ?? 0
    if (scrollTop > 0) return
    startY.current = e.touches[0].clientY
    pulling.current = true
  }, [refreshing])

  const onTouchMove = useCallback((e) => {
    if (!pulling.current || refreshing) return
    const delta = e.touches[0].clientY - startY.current
    if (delta <= 0) {
      setPullDistance(0)
      return
    }
    // Apply resistance curve
    const distance = Math.min(delta * 0.5, MAX_PULL)
    setPullDistance(distance)
    if (distance > 0) {
      e.preventDefault()
    }
  }, [refreshing])

  const onTouchEnd = useCallback(async () => {
    if (!pulling.current) return
    pulling.current = false
    if (pullDistance >= THRESHOLD && onRefresh) {
      setRefreshing(true)
      setPullDistance(THRESHOLD * 0.5)
      try {
        await onRefresh()
      } finally {
        setRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
  }, [pullDistance, onRefresh])

  const indicatorOpacity = Math.min(pullDistance / THRESHOLD, 1)
  const indicatorRotation = (pullDistance / THRESHOLD) * 360

  return (
    <div
      ref={containerRef}
      className="pull-to-refresh-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className={`pull-to-refresh-indicator ${refreshing ? 'refreshing' : ''}`}
        style={{
          height: pullDistance,
          opacity: indicatorOpacity,
        }}
      >
        <div
          className="pull-to-refresh-spinner"
          style={{
            transform: refreshing ? undefined : `rotate(${indicatorRotation}deg)`,
          }}
        />
        <span className="pull-to-refresh-text">
          {refreshing ? 'Refreshing...' : pullDistance >= THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
        </span>
      </div>
      <div
        className="pull-to-refresh-content"
        style={pullDistance > 0 ? { transform: `translateY(${pullDistance}px)` } : undefined}
      >
        {children}
      </div>
    </div>
  )
}

export default PullToRefresh
